/**
 * Phase 5.2c-pre — Layout with active-account dropdown + sandbox banner.
 *
 * Header: brand · nav links (NavLinks rendered inside sidebar) · ActiveAccountSwitcher · user email · logout
 * Below header: sandbox banner (dismissible per-session, only in sandbox mode)
 */
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useAuth, API } from "@/contexts/AuthContext";
import { useActiveAccount } from "@/contexts/ActiveAccountContext";
import { Button } from "@/components/ui/button";
import {
  TrendingUp,
  Package,
  Wand2,
  Film,
  Link as LinkIcon,
  BarChart3,
  LogOut,
  Flame,
  Settings as SettingsIcon,
  ShoppingBag,
  Inbox as InboxIcon,
  ChevronDown,
  TestTube2,
  ExternalLink,
  X,
  Plus,
  Bot,
} from "lucide-react";

const NAV = [
  { to: "/app/trends", label: "Trends", icon: TrendingUp, testid: "nav-trends" },
  { to: "/app/products", label: "Products", icon: Package, testid: "nav-products" },
  { to: "/app/marketplace", label: "Marketplace", icon: ShoppingBag, testid: "nav-marketplace" },
  { to: "/app/inbox", label: "Inbox", icon: InboxIcon, testid: "nav-inbox", showUnread: true },
  { to: "/app/generate", label: "Generate", icon: Wand2, testid: "nav-generate" },
  { to: "/app/library", label: "Library", icon: Film, testid: "nav-library" },
  { to: "/app/links", label: "Links", icon: LinkIcon, testid: "nav-links" },
  { to: "/app/analytics", label: "Analytics", icon: BarChart3, testid: "nav-analytics" },
  { to: "/app/scheduler", label: "Scheduler", icon: Bot, testid: "nav-scheduler" },
  { to: "/app/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

const SANDBOX_DISMISSED_KEY = "sandbox_banner_dismissed";

function SandboxBanner({ environment }) {
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(SANDBOX_DISMISSED_KEY) === "1",
  );
  if (environment !== "sandbox" || dismissed) return null;
  const dismiss = () => {
    sessionStorage.setItem(SANDBOX_DISMISSED_KEY, "1");
    setDismissed(true);
  };
  return (
    <div className="bg-amber-500/10 border-b border-amber-700/40 text-amber-200 text-xs"
         data-testid="sandbox-banner">
      <div className="px-4 sm:px-6 py-2 flex items-center gap-3">
        <TestTube2 className="h-3.5 w-3.5 text-amber-300 shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="font-semibold">Sandbox Mode</span>
          <span className="text-amber-300/80">
            {" "}— TikTok posts are <span className="mono">SELF_ONLY</span> (private). Pending TikTok app review approval.
          </span>{" "}
          <a href="/app/settings/review-readiness"
             className="underline decoration-dotted underline-offset-2 text-amber-100 hover:text-white whitespace-nowrap"
             data-testid="sandbox-banner-submit-link">
            Submit for production review →
          </a>
        </div>
        <button onClick={dismiss}
                className="p-1 hover:bg-amber-500/20 rounded text-amber-300/70 hover:text-amber-200"
                data-testid="sandbox-banner-dismiss"
                aria-label="Dismiss banner">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function ActiveAccountSwitcher() {
  const { accounts, activeAccount, setActiveAccount, loading } = useActiveAccount();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (loading) return null;

  // No accounts connected — CTA link
  if (accounts.length === 0) {
    return (
      <a href="/app/settings"
         className="hidden sm:inline-flex items-center gap-1.5 text-[11px] mono text-amber-300 hover:text-amber-200 px-2 py-1.5 rounded border border-amber-700/50 bg-amber-500/5 transition-colors"
         data-testid="active-account-connect-cta">
        <Plus className="h-3 w-3" /> Connect TikTok in Settings
      </a>
    );
  }

  // Single account — static label, no dropdown
  if (accounts.length === 1) {
    const a = accounts[0];
    return (
      <div className="hidden sm:flex items-center gap-2 text-xs px-2 py-1.5 rounded border border-zinc-800 bg-zinc-900/60"
           data-testid="active-account-static">
        {a.avatar_url
          ? <img src={a.avatar_url} alt="" className="h-5 w-5 rounded-full" />
          : <div className="h-5 w-5 rounded-full bg-zinc-800 text-[9px] flex items-center justify-center text-zinc-500">?</div>}
        <span className="text-zinc-300 mono truncate max-w-[140px]">{a.username ? `@${a.username}` : (a.display_name || `@${(a.open_id || "").slice(0,10)}…`)}</span>
      </div>
    );
  }

  // Multi-account — dropdown
  const active = activeAccount || accounts[0];
  return (
    <div className="relative" ref={ref} data-testid="active-account-switcher">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 text-xs px-2 py-1.5 rounded border border-zinc-700 bg-zinc-900 hover:bg-zinc-800 transition-colors"
        data-testid="active-account-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {active.avatar_url
          ? <img src={active.avatar_url} alt="" className="h-5 w-5 rounded-full" />
          : <div className="h-5 w-5 rounded-full bg-zinc-800 text-[9px] flex items-center justify-center text-zinc-500">?</div>}
        <span className="hidden md:inline text-zinc-400 text-[10px] mono">Active:</span>
        <span className="text-zinc-200 mono truncate max-w-[120px]">{active.username ? `@${active.username}` : (active.display_name || `@${(active.open_id || "").slice(0,10)}…`)}</span>
        <ChevronDown className={`h-3 w-3 text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-64 ap-panel p-1.5 z-40 shadow-xl"
             role="listbox" data-testid="active-account-menu">
          <div className="text-[10px] mono uppercase text-zinc-500 px-2 py-1">
            Switch active TikTok account
          </div>
          {accounts.map((a) => {
            const isActive = a.id === active.id;
            return (
              <button
                key={a.id}
                onClick={() => { setActiveAccount(a.id); setOpen(false); }}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs transition-colors ${
                  isActive
                    ? "bg-emerald-500/10 text-emerald-200 border border-emerald-700/40"
                    : "hover:bg-zinc-900 text-zinc-300 border border-transparent"
                }`}
                data-testid={`active-account-option-${a.id}`}
              >
                {a.avatar_url
                  ? <img src={a.avatar_url} alt="" className="h-6 w-6 rounded-full" />
                  : <div className="h-6 w-6 rounded-full bg-zinc-800 text-[9px] flex items-center justify-center text-zinc-500">?</div>}
                <div className="flex-1 min-w-0 text-left">
                  <div className="text-xs font-medium truncate">{a.display_name || (a.username ? `@${a.username}` : "TikTok account")}</div>
                  <div className="text-[10px] mono text-zinc-500 truncate">{a.username ? `@${a.username}` : `${(a.open_id || "").slice(0, 12)}…`}</div>
                </div>
                {isActive && <span className="text-[10px] text-emerald-400 mono">active</span>}
              </button>
            );
          })}
          <a href="/app/settings"
             className="flex items-center gap-2 px-2 py-1.5 rounded text-xs text-zinc-400 hover:text-zinc-200 hover:bg-zinc-900 border-t border-zinc-800 mt-1 pt-2"
             data-testid="active-account-manage-link">
            <SettingsIcon className="h-3 w-3" /> Manage accounts
            <ExternalLink className="h-3 w-3 ml-auto" />
          </a>
        </div>
      )}
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { environment } = useActiveAccount();
  const nav = useNavigate();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const r = await axios.get(`${API}/inbox/addresses`);
        if (!cancelled) setUnread(r.data?.total_unread || 0);
      } catch (_) { /* ignore */ }
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user]);

  const onLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      {/* Top bar */}
      <header className="border-b border-zinc-800 bg-zinc-950/90 backdrop-blur sticky top-0 z-30">
        <div className="px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-8 w-8 rounded-md bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center shrink-0">
              <Flame className="h-4 w-4 text-emerald-400" />
            </div>
            <div className="min-w-0">
              <div className="text-base font-semibold tracking-tight">AffiliatePilot</div>
              <div className="text-[11px] mono text-zinc-500 truncate">phase 1 · operator console</div>
            </div>
          </div>
          {user && (
            <div className="flex items-center gap-2 sm:gap-3" data-testid="user-menu">
              <ActiveAccountSwitcher />
              <div className="hidden lg:block text-right">
                <div className="text-xs text-zinc-300 truncate max-w-[180px]">{user.email}</div>
                <div className="text-[10px] mono text-zinc-500 uppercase">{user.role}</div>
              </div>
              <Button
                onClick={onLogout}
                variant="outline"
                size="sm"
                className="border-zinc-700 bg-zinc-900 hover:bg-zinc-800 text-zinc-100"
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          )}
        </div>
        {user && <SandboxBanner environment={environment} />}
      </header>

      <div className="flex">
        {/* Sidebar (desktop) */}
        <aside className="hidden md:block w-56 shrink-0 border-r border-zinc-800 min-h-[calc(100vh-57px)] sticky top-[57px]">
          <nav className="p-3 space-y-1" data-testid="sidebar">
            {NAV.map(({ to, label, icon: Icon, testid, showUnread }) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/app/settings"}
                data-testid={testid}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                    isActive
                      ? "bg-emerald-500/10 text-emerald-300 border border-emerald-700/40"
                      : "text-zinc-400 hover:text-zinc-200 hover:bg-zinc-900 border border-transparent"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                <span className="flex-1">{label}</span>
                {showUnread && unread > 0 && (
                  <span className="text-[10px] mono px-1.5 py-0.5 rounded-full bg-emerald-500 text-zinc-950 font-bold"
                        data-testid="nav-inbox-badge">
                    {unread > 99 ? "99+" : unread}
                  </span>
                )}
              </NavLink>
            ))}
            {/* Phase 5.2c-pre — Settings sub-link */}
            <NavLink
              to="/app/settings/review-readiness"
              data-testid="nav-review-readiness"
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 ml-3 rounded-md text-xs transition-colors ${
                  isActive
                    ? "bg-emerald-500/10 text-emerald-300 border border-emerald-700/40"
                    : "text-zinc-500 hover:text-zinc-300 hover:bg-zinc-900 border border-transparent"
                }`
              }
            >
              <span className="text-zinc-700">└</span>
              <span>Review Readiness</span>
            </NavLink>
          </nav>
          <div className="px-3 pt-2 pb-3 border-t border-zinc-900 mt-auto">
            <div className="flex items-center justify-between text-[10px] mono text-zinc-600">
              <a href="/privacy" className="hover:text-zinc-300" data-testid="sidebar-privacy-link">Privacy</a>
              <a href="/terms" className="hover:text-zinc-300" data-testid="sidebar-terms-link">Terms</a>
            </div>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 min-w-0 pb-24 md:pb-6">
          <Outlet />
        </main>
      </div>

      {/* Bottom tabs (mobile) */}
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-30 border-t border-zinc-800 bg-zinc-950/95 backdrop-blur grid grid-cols-9"
        data-testid="bottom-tabs"
      >
        {NAV.map(({ to, label, icon: Icon, testid, showUnread }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={`${testid}-mobile`}
            className={({ isActive }) =>
              `relative flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] ${
                isActive ? "text-emerald-300" : "text-zinc-500"
              }`
            }
          >
            <Icon className="h-4 w-4" />
            <span className="truncate w-full text-center px-1">{label}</span>
            {showUnread && unread > 0 && (
              <span className="absolute top-1 right-2 h-1.5 w-1.5 rounded-full bg-emerald-400" />
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
